import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft, Plus, Upload, Loader2, Trash2, Pause, Play, Pencil,
  ExternalLink, Calendar, Target, X, ShieldAlert, DollarSign, Eye, MousePointerClick, BarChart3, ChevronDown
} from 'lucide-react';
import { API } from '../lib/constants';

const emptyForm = {
  target_type: 'single',
  target_username: '', target_usernames: '', target_states: '', target_cities: '',
  image_url: '', link_url: '', advertiser_name: '',
  start_date: '', end_date: '',
};

const TARGET_TYPES = [
  { id: 'single', label: 'One athlete' },
  { id: 'set', label: 'Several athletes' },
  { id: 'location', label: 'By location' },
  { id: 'all', label: 'All profiles' },
];

const splitList = (s) => (s || '').split(',').map(x => x.trim()).filter(Boolean);
const targetLabel = (ad) => {
  switch (ad.target_type) {
    case 'all': return 'All profiles (blanket)';
    case 'set': return `${(ad.target_usernames || []).length} selected athletes`;
    case 'location': return [...(ad.target_states || []).map(s => s.toUpperCase()), ...(ad.target_cities || [])].join(', ') || 'By location';
    default: return `offersz.co/${ad.target_username}`;
  }
};

const AdminAds = () => {
  const navigate = useNavigate();
  const userId = localStorage.getItem('userId');
  const isAdmin = localStorage.getItem('isAdmin') === 'true';

  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [openStats, setOpenStats] = useState(null);
  const [statsData, setStatsData] = useState({});
  const fileRef = useRef(null);

  const toggleStats = async (adId) => {
    if (openStats === adId) { setOpenStats(null); return; }
    setOpenStats(adId);
    if (!statsData[adId]) {
      try {
        const res = await axios.get(`${API}/api/admin/ads/${adId}/stats`, { params: { admin_user_id: userId } });
        setStatsData(prev => ({ ...prev, [adId]: res.data }));
      } catch { toast.error('Failed to load stats'); }
    }
  };

  const loadAds = async () => {
    try {
      const res = await axios.get(`${API}/api/admin/ads`, { params: { admin_user_id: userId } });
      setAds(res.data.ads || []);
    } catch (e) {
      if (e.response?.status === 403) toast.error('Admin access required');
      else toast.error('Failed to load ads');
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (!userId || !isAdmin) { setLoading(false); return; }
    loadAds();
    // eslint-disable-next-line
  }, []);

  const openCreate = () => { setForm(emptyForm); setEditingId(null); setShowForm(true); };
  const openEdit = (ad) => {
    setForm({
      target_type: ad.target_type || 'single',
      target_username: ad.target_username || '',
      target_usernames: (ad.target_usernames || []).join(', '),
      target_states: (ad.target_states || []).join(', '),
      target_cities: (ad.target_cities || []).join(', '),
      image_url: ad.image_url || '',
      link_url: ad.link_url || '', advertiser_name: ad.advertiser_name || '',
      start_date: ad.start_date || '', end_date: ad.end_date || '',
    });
    setEditingId(ad.id); setShowForm(true);
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/api/admin/ads/upload-creative`, fd, {
        params: { admin_user_id: userId },
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setForm(p => ({ ...p, image_url: res.data.image_url }));
      toast.success('Creative uploaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally { setUploading(false); }
  };

  const save = async () => {
    const t = form.target_type;
    if (t === 'single' && !form.target_username.trim()) { toast.error('Enter the target player username'); return; }
    if (t === 'set' && splitList(form.target_usernames).length === 0) { toast.error('Enter at least one username'); return; }
    if (t === 'location' && splitList(form.target_states).length === 0 && splitList(form.target_cities).length === 0) { toast.error('Enter at least one state or city'); return; }
    if (!form.image_url) { toast.error('Upload a creative image'); return; }
    if (!form.link_url.trim()) { toast.error('Destination link is required'); return; }
    if (!form.start_date || !form.end_date) { toast.error('Set start and end dates'); return; }
    if (form.end_date < form.start_date) { toast.error('End date must be after start date'); return; }
    const payload = {
      target_type: t,
      target_username: t === 'single' ? form.target_username.trim() : '',
      target_usernames: t === 'set' ? splitList(form.target_usernames) : [],
      target_states: t === 'location' ? splitList(form.target_states) : [],
      target_cities: t === 'location' ? splitList(form.target_cities) : [],
      image_url: form.image_url, link_url: form.link_url, advertiser_name: form.advertiser_name,
      start_date: form.start_date, end_date: form.end_date,
    };
    setSaving(true);
    try {
      if (editingId) {
        await axios.put(`${API}/api/admin/ads/${editingId}`, payload, { params: { admin_user_id: userId } });
        toast.success('Ad updated');
      } else {
        await axios.post(`${API}/api/admin/ads`, payload, { params: { admin_user_id: userId } });
        toast.success('Ad created');
      }
      setShowForm(false); setEditingId(null); setForm(emptyForm);
      loadAds();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save ad');
    } finally { setSaving(false); }
  };

  const toggleStatus = async (ad) => {
    const next = ad.status === 'active' ? 'paused' : 'active';
    try {
      await axios.put(`${API}/api/admin/ads/${ad.id}`, { status: next }, { params: { admin_user_id: userId } });
      toast.success(next === 'active' ? 'Ad resumed' : 'Ad paused');
      loadAds();
    } catch { toast.error('Failed'); }
  };

  const remove = async (ad) => {
    if (!window.confirm('Remove this ad placement?')) return;
    try {
      await axios.delete(`${API}/api/admin/ads/${ad.id}`, { params: { admin_user_id: userId } });
      toast.success('Ad removed');
      loadAds();
    } catch { toast.error('Failed'); }
  };

  const resolveImg = (u) => (u && u.startsWith('/api') ? `${API}${u}` : u);

  if (!userId || !isAdmin) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] text-white flex items-center justify-center px-4 text-center" data-testid="admin-access-denied">
        <div>
          <ShieldAlert className="h-12 w-12 text-[#16A34A] mx-auto mb-4" />
          <h1 className="text-2xl font-black mb-2">Admin Access Only</h1>
          <p className="text-white/40 mb-6">You don't have permission to manage ads.</p>
          <button onClick={() => navigate('/dashboard')} className="bg-[#0F5132] text-white font-bold px-8 py-3 rounded-full">Back to Dashboard</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      {/* Header */}
      <div className="border-b border-white/10 sticky top-0 z-40 bg-[#0a0a0a]/90 backdrop-blur-md">
        <div className="max-w-5xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/dashboard')} className="text-white/40 hover:text-white" data-testid="admin-back-btn"><ArrowLeft className="h-5 w-5" /></button>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-[#16A34A] rounded-full flex items-center justify-center"><DollarSign className="h-4 w-4 text-black" /></div>
              <span className="font-black text-lg">Ad Manager</span>
            </div>
          </div>
          <button onClick={openCreate} data-testid="new-ad-btn"
            className="bg-[#0F5132] text-white font-bold px-4 py-2 rounded-full text-sm flex items-center gap-1.5 hover:bg-[#0B3B24]">
            <Plus className="h-4 w-4" /> New Ad
          </button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="mb-6">
          <h1 className="text-2xl font-black">Sold Ad Placements</h1>
          <p className="text-white/40 text-sm">Target one athlete, several, a location, or all profiles — schedule it and it goes live automatically.</p>
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-[#16A34A] border-t-transparent rounded-full animate-spin" /></div>
        ) : ads.length === 0 ? (
          <div className="border border-dashed border-white/15 rounded-2xl py-16 text-center" data-testid="ads-empty">
            <Target className="h-10 w-10 text-white/20 mx-auto mb-3" />
            <p className="text-white/50 font-semibold">No ad placements yet</p>
            <p className="text-white/30 text-sm mb-5">Create your first sold ad to start monetizing profiles.</p>
            <button onClick={openCreate} className="bg-[#0F5132] text-white font-bold px-6 py-2.5 rounded-full text-sm">Create Ad</button>
          </div>
        ) : (
          <div className="grid gap-3" data-testid="ads-list">
            {ads.map(ad => (
              <div key={ad.id} data-testid={`ad-row-${ad.id}`}
                className="bg-white/[0.04] border border-white/10 rounded-2xl p-4 flex flex-col sm:flex-row gap-4 items-start">
                <div className="w-full sm:w-40 h-24 rounded-xl overflow-hidden bg-black/40 flex-shrink-0">
                  {ad.image_url && <img src={resolveImg(ad.image_url)} alt="" className="w-full h-full object-cover" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="font-bold text-base">{ad.advertiser_name || 'Untitled ad'}</span>
                    {ad.is_live
                      ? <span className="bg-[#0F5132] text-white text-[10px] font-black px-2 py-0.5 rounded-full">LIVE</span>
                      : ad.status === 'paused'
                        ? <span className="bg-white/10 text-white/50 text-[10px] font-bold px-2 py-0.5 rounded-full">PAUSED</span>
                        : <span className="bg-amber-500/20 text-amber-400 text-[10px] font-bold px-2 py-0.5 rounded-full">SCHEDULED</span>}
                  </div>
                  <div className="flex items-center gap-1.5 text-white/50 text-xs mb-0.5" data-testid={`ad-target-${ad.id}`}><Target className="h-3 w-3" /> {targetLabel(ad)}</div>
                  <div className="flex items-center gap-1.5 text-white/40 text-xs mb-0.5"><Calendar className="h-3 w-3" /> {ad.start_date} → {ad.end_date}</div>
                  <a href={ad.link_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-[#16A34A] text-xs hover:underline truncate"><ExternalLink className="h-3 w-3" /> {ad.link_url}</a>
                  <div className="flex items-center gap-3 mt-2 flex-wrap">
                    <span className="flex items-center gap-1 text-white/60 text-xs" data-testid={`ad-impressions-${ad.id}`}><Eye className="h-3.5 w-3.5" /> {ad.impressions || 0} impressions</span>
                    <span className="flex items-center gap-1 text-white/60 text-xs" data-testid={`ad-clicks-${ad.id}`}><MousePointerClick className="h-3.5 w-3.5" /> {ad.clicks || 0} clicks</span>
                    <span className="flex items-center gap-1 text-[#4ade80] text-xs font-bold"><BarChart3 className="h-3.5 w-3.5" /> {ad.ctr || 0}% CTR</span>
                    <button onClick={() => toggleStats(ad.id)} data-testid={`ad-stats-toggle-${ad.id}`} className="flex items-center gap-1 text-white/40 text-xs hover:text-white">By player <ChevronDown className={`h-3 w-3 transition-transform ${openStats === ad.id ? 'rotate-180' : ''}`} /></button>
                  </div>
                  {openStats === ad.id && (
                    <div className="mt-2 bg-black/30 rounded-xl p-3" data-testid={`ad-stats-panel-${ad.id}`}>
                      {!statsData[ad.id] ? <p className="text-white/30 text-xs">Loading…</p> :
                        statsData[ad.id].by_player?.length === 0 ? <p className="text-white/30 text-xs">No activity yet.</p> : (
                        <table className="w-full text-xs">
                          <thead><tr className="text-white/30 text-left"><th className="font-medium pb-1">Player</th><th className="font-medium pb-1 text-right">Impr.</th><th className="font-medium pb-1 text-right">Clicks</th><th className="font-medium pb-1 text-right">CTR</th></tr></thead>
                          <tbody>
                            {statsData[ad.id].by_player.map((r) => (
                              <tr key={r.username} className="text-white/70 border-t border-white/5"><td className="py-1">offersz.co/{r.username}</td><td className="py-1 text-right">{r.impressions}</td><td className="py-1 text-right">{r.clicks}</td><td className="py-1 text-right text-[#4ade80]">{r.ctr}%</td></tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  )}
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  <button onClick={() => toggleStatus(ad)} data-testid={`ad-toggle-${ad.id}`} title={ad.status === 'active' ? 'Pause' : 'Resume'}
                    className="p-2 rounded-full bg-white/5 hover:bg-white/15 text-white/70">
                    {ad.status === 'active' ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                  </button>
                  <button onClick={() => openEdit(ad)} data-testid={`ad-edit-${ad.id}`} title="Edit"
                    className="p-2 rounded-full bg-white/5 hover:bg-white/15 text-white/70"><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => remove(ad)} data-testid={`ad-delete-${ad.id}`} title="Remove"
                    className="p-2 rounded-full bg-red-500/10 hover:bg-red-500/20 text-red-400"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center" onClick={() => setShowForm(false)}>
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
          <div className="relative w-full max-w-lg mx-4 mb-4 sm:mb-0 bg-zinc-900 border border-white/10 rounded-3xl p-6 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="ad-form-modal">
            <button onClick={() => setShowForm(false)} className="absolute top-4 right-4 text-white/30"><X className="h-5 w-5" /></button>
            <h3 className="font-black text-lg mb-5">{editingId ? 'Edit Ad Placement' : 'New Ad Placement'}</h3>
            <div className="space-y-4">
              {/* Creative */}
              <div>
                <label className="text-xs font-semibold text-white/60 uppercase tracking-wider">Creative</label>
                <div className="mt-2 flex items-center gap-3">
                  <div className="w-28 h-20 rounded-xl overflow-hidden bg-black/40 border border-white/10 flex items-center justify-center flex-shrink-0">
                    {form.image_url ? <img src={resolveImg(form.image_url)} alt="" className="w-full h-full object-cover" /> : <Upload className="h-5 w-5 text-white/20" />}
                  </div>
                  <div>
                    <input ref={fileRef} type="file" accept="image/*" onChange={handleUpload} className="hidden" data-testid="ad-creative-input" />
                    <button onClick={() => fileRef.current?.click()} disabled={uploading}
                      className="bg-white/10 hover:bg-white/20 text-white text-sm font-semibold px-4 py-2 rounded-xl flex items-center gap-2">
                      {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                      {form.image_url ? 'Replace' : 'Upload image'}
                    </button>
                    <p className="text-white/30 text-[11px] mt-1">PNG/JPG/WebP, max 5MB</p>
                  </div>
                </div>
              </div>
              <Field label="Advertiser name" value={form.advertiser_name} onChange={v => setForm(p => ({ ...p, advertiser_name: v }))} placeholder="Joe's Sports Shop" testid="ad-advertiser-input" />

              {/* Targeting */}
              <div>
                <label className="text-xs font-semibold text-white/60 uppercase tracking-wider">Who sees this ad?</label>
                <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {TARGET_TYPES.map(t => (
                    <button key={t.id} type="button" onClick={() => setForm(p => ({ ...p, target_type: t.id }))}
                      data-testid={`ad-targettype-${t.id}`}
                      className={`text-xs font-bold py-2 px-2 rounded-xl border transition-colors ${form.target_type === t.id ? 'bg-[#0F5132] border-[#16A34A] text-white' : 'bg-white/5 border-white/10 text-white/60 hover:bg-white/10'}`}>
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>

              {form.target_type === 'single' && (
                <Field label="Target player username *" value={form.target_username} onChange={v => setForm(p => ({ ...p, target_username: v.replace(/[^a-zA-Z0-9_-]/g, '') }))} placeholder="dcook1" prefix="offersz.co/" testid="ad-target-input" />
              )}
              {form.target_type === 'set' && (
                <Field label="Player usernames (comma-separated) *" value={form.target_usernames} onChange={v => setForm(p => ({ ...p, target_usernames: v }))} placeholder="dcook1, jsmith27, mjones" testid="ad-usernames-input" />
              )}
              {form.target_type === 'location' && (
                <div className="grid grid-cols-1 gap-3">
                  <Field label="States (comma-separated, e.g. TX, FL)" value={form.target_states} onChange={v => setForm(p => ({ ...p, target_states: v }))} placeholder="TX, FL" testid="ad-states-input" />
                  <Field label="Cities (comma-separated)" value={form.target_cities} onChange={v => setForm(p => ({ ...p, target_cities: v }))} placeholder="Austin, Miami" testid="ad-cities-input" />
                  <p className="text-white/30 text-[11px] -mt-1">Great for local sponsors (e.g. restaurants) targeting athletes near them. A profile matches if its state or city is listed.</p>
                </div>
              )}
              {form.target_type === 'all' && (
                <div className="bg-[#16A34A]/10 border border-[#16A34A]/20 rounded-xl p-3 text-xs text-white/70" data-testid="ad-all-note">
                  This ad shows on <span className="font-bold">every public profile</span>, including new ones. An athlete-specific ad will override it on that profile.
                </div>
              )}

              <Field label="Destination link *" value={form.link_url} onChange={v => setForm(p => ({ ...p, link_url: v }))} placeholder="https://sponsor.com" testid="ad-link-input" />
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-white/60 uppercase tracking-wider">Start date *</label>
                  <input type="date" value={form.start_date} onChange={e => setForm(p => ({ ...p, start_date: e.target.value }))} data-testid="ad-start-input"
                    className="mt-2 w-full px-3 py-2.5 rounded-xl text-sm bg-white/5 border border-white/10 text-white focus:outline-none focus:ring-2 focus:ring-[#16A34A]/30 [color-scheme:dark]" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-white/60 uppercase tracking-wider">End date *</label>
                  <input type="date" value={form.end_date} onChange={e => setForm(p => ({ ...p, end_date: e.target.value }))} data-testid="ad-end-input"
                    className="mt-2 w-full px-3 py-2.5 rounded-xl text-sm bg-white/5 border border-white/10 text-white focus:outline-none focus:ring-2 focus:ring-[#16A34A]/30 [color-scheme:dark]" />
                </div>
              </div>
              <button onClick={save} disabled={saving} data-testid="ad-save-btn"
                className="w-full bg-[#0F5132] hover:bg-[#0B3B24] text-white font-bold py-3 rounded-xl text-sm disabled:opacity-50 flex items-center justify-center gap-2">
                {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                {editingId ? 'Save Changes' : 'Create Ad'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Field = ({ label, value, onChange, placeholder, prefix, testid }) => (
  <div>
    <label className="text-xs font-semibold text-white/60 uppercase tracking-wider">{label}</label>
    <div className={`mt-2 flex items-center rounded-xl bg-white/5 border border-white/10 focus-within:ring-2 focus-within:ring-[#16A34A]/30`}>
      {prefix && <span className="text-white/30 text-sm pl-3">{prefix}</span>}
      <input value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} data-testid={testid}
        className="flex-1 px-3 py-2.5 rounded-xl text-sm bg-transparent text-white placeholder:text-white/30 focus:outline-none" />
    </div>
  </div>
);

export default AdminAds;
